import { useEffect, useState } from "react";
import { api } from "../api";
import { Link } from "../router";
import { code128Bars } from "../barcode";

const TIMING: Record<string, string> = { after_food: "after food", before_food: "before food", with_food: "with food", empty_stomach: "on an empty stomach", bedtime: "at bedtime", any: "" };
const SEX: Record<string, string> = { female: "F", male: "M", other: "O", unknown: "" };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const day = (d: string) => `${d.slice(8, 10)} ${MONTHS[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;
/** +911123456789 -> +91 11 2345 6789; +919876543210 -> +91 98765 43210 */
const phone = (e164: string) => { const m = /^\+91(\d{10})$/.exec(e164); if (!m) return e164; const d = m[1]!;
  return /^[6-9]/.test(d) ? `+91 ${d.slice(0, 5)} ${d.slice(5)}` : `+91 ${d.slice(0, 2)} ${d.slice(2, 6)} ${d.slice(6)}`; };
const istDay = (iso: string) => day(new Date(new Date(iso).getTime() + 330 * 60_000).toISOString().slice(0, 10));

function Letterhead({ l, doctor }: { l: any; doctor: any }) {
  return (
    <header className="lh">
      <div>
        <div className="lh-name">{l.name}</div>
        <div className="lh-addr">{[l.addressLine1, l.city, l.pin].filter(Boolean).join(", ")}{l.phone ? ` · ${phone(l.phone)}` : ""}{l.email ? ` · ${l.email}` : ""}</div>
        {l.registrationNumber && <div className="lh-addr">Registration: {l.registrationNumber}</div>}
      </div>
      {doctor && <div className="lh-doc"><strong>{doctor.displayName}</strong><div>{doctor.qualifications}</div><div>Reg. No. {doctor.registrationNumber}{doctor.registrationCouncil ? `, ${doctor.registrationCouncil}` : ""}</div></div>}
    </header>
  );
}
function Toolbar({ back }: { back: string }) {
  return <div className="print-toolbar no-print"><Link to={back} className="btn btn-quiet">Back</Link><button className="btn btn-primary" onClick={() => window.print()}>Print or save as PDF</button></div>;
}

export function PrintPrescription({ id }: { id: string }) {
  const [d, setD] = useState<any>(null), [err, setErr] = useState<string | null>(null);
  useEffect(() => { api.authed(`/api/v1/print/prescription/${id}`).then(setD).catch((e) => setErr(e.message)); }, [id]);
  if (err) return <p className="print-error">{err}</p>;
  if (!d) return null;
  const e = d.encounter, v = d.vitals;
  return (
    <div className="print-page">
      <Toolbar back={`/app/consultations/${id}`} />
      <article className="sheet" aria-label="Prescription">
        <Letterhead l={d.letterhead} doctor={d.practitioner} />
        <div className="pt-row">
          <span><strong>{d.patient.fullName}</strong>, {d.patient.age}{SEX[d.patient.sex] ? ` / ${SEX[d.patient.sex]}` : ""}</span>
          <span>UHID {d.patient.uhid}</span><span>{istDay(e.signedAt)}</span>
        </div>
        {d.allergies.length > 0 && <p className="pr-allergy">Allergies: {d.allergies.join(", ")}</p>}
        {v && <p className="pr-small">Vitals: {[v.systolic ? `BP ${v.systolic}/${v.diastolic} mmHg` : null, v.pulse ? `Pulse ${v.pulse}/min` : null, v.temperatureC ? `Temp ${v.temperatureC}°C` : null,
          v.spo2 ? `SpO₂ ${v.spo2}%` : null, v.weightKg ? `Wt ${v.weightKg} kg` : null].filter(Boolean).join(" · ")}</p>}
        {e.chiefComplaint && <p><span className="pr-label">Complaints:</span> {e.chiefComplaint}</p>}
        {e.examination && <p><span className="pr-label">Findings:</span> {e.examination}</p>}
        {d.diagnoses.length > 0 && <p><span className="pr-label">Diagnosis:</span> {d.diagnoses.map((x: any) => `${x.label}${x.code ? ` (${x.code})` : ""}${x.kind === "provisional" ? " — provisional" : ""}`).join("; ")}</p>}
        {d.items.length > 0 && <>
          <div className="rx-symbol" aria-hidden="true">℞</div>
          <table className="rx-table">
            <thead><tr><th>#</th><th>Medicine</th><th>Dose and frequency</th><th>Duration</th><th>Qty</th></tr></thead>
            <tbody>{d.items.map((i: any, n: number) => (
              <tr key={n}><td>{n + 1}</td>
                <td><strong>{i.medicine}</strong>{i.strength ? ` ${i.strength}` : ""} <span className="pr-small">{i.form}</span>{i.generic && <div className="pr-small">({i.generic})</div>}</td>
                <td>{i.dose ? `${i.dose}, ` : ""}{i.frequency} <span className="pr-small">({i.frequencyLabel}{TIMING[i.timing] ? `, ${TIMING[i.timing]}` : ""})</span>{i.instructions && <div className="pr-small">{i.instructions}</div>}</td>
                <td>{i.continuous ? "Continue" : i.durationValue ? `${i.durationValue} ${i.durationUnit}` : "As directed"}</td>
                <td>{i.quantity ?? ""}</td></tr>))}
            </tbody>
          </table>
        </>}
        {d.orders.length > 0 && <p><span className="pr-label">Investigations / referrals:</span> {d.orders.map((o: any) => o.name + (o.urgency !== "routine" ? ` (${o.urgency.toUpperCase()})` : "")).join(", ")}</p>}
        {e.advice && <p><span className="pr-label">Advice:</span> {e.advice}</p>}
        {e.referral && <p><span className="pr-label">Referral:</span> {e.referral}</p>}
        {e.followUpDate && <p><span className="pr-label">Follow-up:</span> {day(e.followUpDate)}{e.followUpNote ? `, ${e.followUpNote}` : ""}</p>}
        <footer className="sig">
          <div className="sig-line">{d.practitioner?.displayName}</div>
          <div className="pr-small">Electronically signed on {istDay(e.signedAt)} · Verification code {d.code}</div>
        </footer>
      </article>
    </div>
  );
}

export function PrintCertificate({ id }: { id: string }) {
  const [d, setD] = useState<any>(null), [err, setErr] = useState<string | null>(null);
  useEffect(() => { api.authed(`/api/v1/print/certificate/${id}`).then(setD).catch((e) => setErr(e.message)); }, [id]);
  if (err) return <p className="print-error">{err}</p>;
  if (!d) return null;
  const c = d.certificate;
  const title = c.kind === "sick_leave" ? "Medical certificate for leave" : c.kind === "fitness" ? "Certificate of fitness" : "Medical certificate";
  return (
    <div className="print-page">
      <Toolbar back={c.encounterId ? `/app/consultations/${c.encounterId}` : "/app"} />
      <article className="sheet" aria-label={title}>
        <Letterhead l={d.letterhead} doctor={d.practitioner} />
        <h1 className="cert-title">{title}</h1>
        <p className="pr-small">No. {c.number} · {istDay(c.issuedAt)}</p>
        <p className="cert-body">
          This is to certify that <strong>{d.patient.fullName}</strong> ({d.patient.age}, UHID {d.patient.uhid}) was examined by me
          {c.kind === "fitness" ? " and is medically fit to resume normal duties" :
            ` and was advised rest from ${day(c.fromDate)} to ${day(c.toDate)}${c.diagnosisText ? `, on account of ${c.diagnosisText}` : ""}`}.
        </p>
        {c.remarks && <p className="cert-body">{c.remarks}</p>}
        <footer className="sig">
          <div className="sig-line">{d.practitioner?.displayName}</div>
          <div className="pr-small">Reg. No. {d.practitioner?.registrationNumber} · Electronically signed · Verification code {d.code}</div>
        </footer>
      </article>
    </div>
  );
}

const inrP = (p: number) => { const r = String(Math.floor(Math.abs(p) / 100)); const g = r.length <= 3 ? r : r.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + r.slice(-3); return `${p < 0 ? "-" : ""}₹${g}.${String(Math.abs(p) % 100).padStart(2, "0")}`; };

export function PrintInvoice({ id }: { id: string }) {
  const [d, setD] = useState<any>(null), [err, setErr] = useState<string | null>(null);
  useEffect(() => { api.authed(`/api/v1/print/invoice/${id}`).then(setD).catch((e) => setErr(e.message)); }, [id]);
  if (err) return <p className="print-error">{err}</p>;
  if (!d) return null;
  const inv = d.invoice, l = d.letterhead, gst = inv.cgst + inv.sgst + inv.igst > 0;
  return (
    <div className="print-page">
      <Toolbar back={`/app/bills/${id}`} />
      <article className="sheet" aria-label={inv.documentTitle}>
        <Letterhead l={l} doctor={null} />
        {l.gstin && <p className="pr-small">GSTIN: {l.gstin}</p>}
        <h1 className="cert-title">{inv.documentTitle}{inv.status === "cancelled" ? " (CANCELLED)" : ""}</h1>
        <div className="pt-row"><span>No. <strong>{inv.number}</strong></span><span>{istDay(inv.issuedAt)}</span></div>
        <div className="pt-row"><span><strong>{d.patient?.fullName}</strong>, {d.patient?.age}</span><span>UHID {d.patient?.uhid}</span>{l.branch && l.branch !== l.name && <span>{l.branch}</span>}</div>
        <table className="rx-table">
          <thead><tr><th>#</th><th>Description</th><th>SAC</th><th className="num">Qty</th><th className="num">Rate</th><th className="num">Taxable</th>{gst && <th className="num">GST</th>}<th className="num">Amount</th></tr></thead>
          <tbody>{inv.lines.map((x: any, i: number) => (
            <tr key={x.id}><td>{i + 1}</td><td>{x.description}</td><td>{x.sacCode ?? ""}</td><td className="num">{x.quantity}</td><td className="num">{inrP(x.unitPrice)}</td><td className="num">{inrP(x.taxable)}</td>
              {gst && <td className="num">{x.rateBp ? `${x.rateBp / 100}% ${inrP(x.cgst + x.sgst + x.igst)}` : "Exempt"}</td>}<td className="num">{inrP(x.total)}</td></tr>))}
          </tbody>
        </table>
        <table className="sum-table"><tbody>
          <tr><td>Subtotal</td><td className="num">{inrP(inv.subtotal)}</td></tr>
          {inv.discountTotal > 0 && <tr><td>Discount</td><td className="num">-{inrP(inv.discountTotal)}</td></tr>}
          {inv.cgst > 0 && <tr><td>CGST</td><td className="num">{inrP(inv.cgst)}</td></tr>}{inv.sgst > 0 && <tr><td>SGST</td><td className="num">{inrP(inv.sgst)}</td></tr>}
          {inv.igst > 0 && <tr><td>IGST</td><td className="num">{inrP(inv.igst)}</td></tr>}{inv.roundOff !== 0 && <tr><td>Round off</td><td className="num">{inrP(inv.roundOff)}</td></tr>}
          <tr className="grand"><td>Total</td><td className="num">{inrP(inv.total)}</td></tr>
          <tr><td>Paid</td><td className="num">{inrP(d.balance.paid - d.balance.refunded)}</td></tr><tr><td>Balance due</td><td className="num">{inrP(d.balance.due)}</td></tr>
        </tbody></table>
        <p className="pr-small">{d.totalInWords}</p>
        {inv.documentTitle === "Bill of Supply" && <p className="pr-small">Supply of exempt healthcare services; no GST charged.</p>}
        {inv.creditNoteNumber && <p className="pr-allergy">Cancelled by credit note {inv.creditNoteNumber}: {inv.cancelReason}</p>}
        <footer className="sig"><div className="sig-line">Authorised signatory</div><div className="pr-small">Computer-generated document.</div></footer>
      </article>
    </div>
  );
}

export function PrintReceipt({ id, paymentId }: { id: string; paymentId: string }) {
  const [d, setD] = useState<any>(null), [err, setErr] = useState<string | null>(null);
  useEffect(() => { api.authed(`/api/v1/print/receipt/${id}/${paymentId}`).then(setD).catch((e) => setErr(e.message)); }, [id, paymentId]);
  if (err) return <p className="print-error">{err}</p>;
  if (!d) return null;
  const p = d.payment;
  const mode: Record<string, string> = { cash: "cash", upi: "UPI", card: "card", bank_transfer: "bank transfer", cheque: "cheque", online: "online payment" };
  return (
    <div className="print-page">
      <Toolbar back={`/app/bills/${id}`} />
      <article className="sheet receipt" aria-label="Payment receipt">
        <Letterhead l={d.letterhead} doctor={null} />
        <h1 className="cert-title">Payment receipt</h1>
        <div className="pt-row"><span>Receipt <strong>{p.receiptNumber}</strong></span><span>{istDay(p.receivedAt)}</span></div>
        <p className="cert-body">Received with thanks from <strong>{d.patient?.fullName}</strong> (UHID {d.patient?.uhid}) the sum of <strong>{inrP(p.amount)}</strong> ({d.amountInWords}) by {mode[p.mode]}{p.reference ? `, reference ${p.reference}` : ""}, against bill {d.invoice.number}.</p>
        <footer className="sig"><div className="sig-line">Cashier</div><div className="pr-small">Computer-generated receipt.</div></footer>
      </article>
    </div>
  );
}

const FLAGP: Record<string, string> = { L: "L", H: "H", LL: "L*", HH: "H*", A: "Abn" };
export function PrintLabReport({ id }: { id: string }) {
  const [d, setD] = useState<any>(null), [err, setErr] = useState<string | null>(null);
  useEffect(() => { api.authed(`/api/v1/print/lab/${id}`).then(setD).catch((e) => setErr(e.message)); }, [id]);
  if (err) return <p className="print-error">{err}</p>;
  if (!d) return null;
  const SEXL: Record<string, string> = { female: "Female", male: "Male", other: "Other" };
  return (
    <div className="print-page">
      <Toolbar back={`/app/lab/${id}`} />
      <article className="sheet" aria-label="Laboratory report">
        <Letterhead l={d.letterhead} doctor={null} />
        <h1 className="cert-title">Laboratory report</h1>
        <div className="pt-row"><span><strong>{d.patient?.fullName}</strong>, {d.patient?.age}{d.patient?.sex ? `, ${SEXL[d.patient.sex] ?? ""}` : ""}</span><span>UHID {d.patient?.uhid}</span></div>
        <div className="pt-row"><span>Accession <strong className="mono">{d.accessionNo}</strong></span><span>Registered {d.registered}</span>{d.referredBy && <span>Referred by {d.referredBy}</span>}</div>
        {d.tests.map((t: any, i: number) => (
          <section key={i} className="lab-test">
            <h2>{t.name}{t.amended ? " — AMENDED REPORT" : ""}</h2>
            <p className="pr-small">Sample: {t.sampleType}{t.method ? ` · Method: ${t.method}` : ""} · Collected {t.collected ?? "—"} · Reported {t.reported}</p>
            <table className="rx-table lab-table"><thead><tr><th>Investigation</th><th>Result</th><th>Flag</th><th>Unit</th><th>Reference range</th></tr></thead>
              <tbody>{t.results.map((v: any) => <tr key={v.code} className={v.flag ? "flagged" : ""}><td>{v.name}</td><td><strong>{v.value}</strong></td><td>{v.flag ? FLAGP[v.flag] : ""}</td>
                <td>{v.unit ?? ""}</td><td>{v.rangeText ?? ""}</td></tr>)}</tbody></table>
            {t.comment && <p className="pr-small">Comment: {t.comment}</p>}
            {t.amended && <p className="pr-small">Amended: {t.amendReason}. This report replaces any earlier version.</p>}
            <p className="pr-small">Verified by {t.verifiedBy}</p>
          </section>))}
        {d.pending.length > 0 && <p className="pr-small">Pending: {d.pending.join(", ")}. A further report will follow.</p>}
        <p className="pr-small">L/H: below/above the reference range; * critical value. Reference ranges depend on the method, age and sex; interpret results with the clinical picture.
          Electronically verified report.</p>
      </article>
    </div>
  );
}

export function PrintLabLabels({ id }: { id: string }) {
  const [r, setR] = useState<any>(null), [err, setErr] = useState<string | null>(null);
  useEffect(() => { api.authed(`/api/v1/lab/requests/${id}`).then((x) => setR(x.request)).catch((e) => setErr(e.message)); }, [id]);
  if (err) return <p className="print-error">{err}</p>;
  if (!r) return null;
  const samples = [...new Map(r.tests.filter((t: any) => t.status !== "cancelled").map((t: any) => [t.sampleType, t])).values()] as any[];
  return (
    <div className="print-page">
      <Toolbar back={`/app/lab/${id}`} />
      <div className="labels" aria-label="Sample tube labels">
        {samples.map((s) => <div key={s.sampleType} className="tube-label">
          <Barcode text={r.accessionNo} />
          <div className="tl-text"><strong className="mono">{r.accessionNo}</strong> <span>{s.container}</span></div>
          <div className="tl-text"><span>{r.patient?.fullName}</span> <span>{r.patient?.age}</span></div>
          <div className="tl-text small">{r.tests.filter((t: any) => t.sampleType === s.sampleType && t.status !== "cancelled").map((t: any) => t.code).join(" ")}</div>
        </div>)}
      </div>
    </div>
  );
}
function Barcode({ text }: { text: string }) {
  const { bars, width } = code128Bars(text);
  return <svg className="barcode" viewBox={`0 0 ${width} 40`} preserveAspectRatio="none" role="img" aria-label={`Barcode ${text}`}>
    {bars.map(([x, w], i) => <rect key={i} x={x} y={0} width={w} height={40} fill="#000" />)}</svg>;
}
